import {
  useEffect,
  useRef,
  useState,
  Dispatch,
  SetStateAction,
  useImperativeHandle,
  forwardRef,
} from "react";
import classnames from "classnames";
import { useGalleryGesture } from "./useGalleryGesture";
import { scrollElementIntoViewWithinContainer } from "../../../../../../utils/scroll/scroll";
import styles from "./ImageElement.module.scss";
import { GalleryImageObject } from "../../../../utils";

export interface ImageElementProps {
  image: GalleryImageObject;
  setModalHasInteraction: Dispatch<SetStateAction<boolean>>;
  isSelectedIndex: boolean;
}

export interface ImageElementRef {
  reset: () => void;
  getScale: () => number;
}

export const ImageElement = forwardRef<ImageElementRef, ImageElementProps>(
  (props, ref) => {
    const { image, isSelectedIndex } = props;
    const wrapperRef = useRef<HTMLDivElement>(null);
    const imageRef = useRef<HTMLImageElement>(null);
    const [firstImageLoaded, setFirstImageLoaded] = useState<boolean>(false);
    const [largeImageLoaded, setlargeImageLoaded] =
      useState<boolean>(isSelectedIndex);
    const firstRender = useRef(true);

    const [scale, drag, resetAdjustments] = useGalleryGesture(
      wrapperRef,
      imageRef,
    );
    const [isSuperZoomed, setIsSuperZoomed] = useState<boolean>(false);

    useImperativeHandle(ref, () => ({
      reset() {
        resetAdjustments();
        setIsSuperZoomed(false);
      },
      getScale() {
        return scale;
      },
    }));

    const centerVertically = () => {
      scrollElementIntoViewWithinContainer({
        container: wrapperRef.current!,
        direction: "vertical",
        element: imageRef.current!,
        mode: "middle",
        behavior: "auto",
      });
      setFirstImageLoaded(true);
    };

    useEffect(() => {
      resetAdjustments();
    }, []);

    useEffect(() => {
      if (firstRender.current) {
        firstRender.current = false;
        return;
      }
      props.setModalHasInteraction(true);
    }, [scale]);

    const imgOnLoad = () => {
      if (!firstImageLoaded) {
        centerVertically();
      }

      if (!isSelectedIndex && !largeImageLoaded) {
        const largeImage = new Image();
        largeImage.src = imageRef!.current!.getAttribute("data-src") || "";
        largeImage.onload = () => {
          imageRef!.current!.src = largeImage.src;
          setlargeImageLoaded(true);
        };
      }
    };

    return (
      <div
        ref={wrapperRef}
        className={styles.innerWrapper}
        draggable
        data-testid="gallery:zoom:modal:image:drag-area"
      >
        <img
          ref={imageRef}
          data-testid="gallery:zoom:modal:image"
          src={`${image.imageUrl}${
            isSelectedIndex || largeImageLoaded
              ? "?wid=1440&fit=constrain"
              : "?$rsp-pdp-port-640-82$"
          }`}
          data-src={`${image.imageUrl}?wid=1440&fit=constrain`}
          data-super-zoom={isSuperZoomed}
          alt=""
          onLoad={imgOnLoad}
          className={classnames(styles.image, {
            [styles.hidden]: !firstImageLoaded,
            [styles.absolute]: isSuperZoomed,
          })}
          style={{
            left: drag[0],
            top: drag[1],
            transform: `scale(${scale})`,
          }}
          onClick={(e) => {
            const img = e.currentTarget;
            const perc = e.clientY / img.height;
            const scrollHeight = wrapperRef.current?.scrollHeight;
            const distance = (scrollHeight || 0) * perc;
            setIsSuperZoomed(!isSuperZoomed);
            wrapperRef.current?.scrollTo({
              top: distance,
              behavior: "smooth",
            });
            resetAdjustments();
          }}
        />
      </div>
    );
  },
);

ImageElement.displayName = "ImageElement";
